// Galería del hero con miniaturas y visor ampliado.
// Componente pdp-*: el HTML llega renderizado desde Liquid y este módulo solo añade interacción.

const define = (name, element) => {
  if (!customElements.get(name)) customElements.define(name, element);
};
const lockScroll = (locked) => {
  document.documentElement.style.overflow = locked ? 'hidden' : '';
};

/* ---------- Galería con miniaturas y visor ---------- */
class PdpGallery extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-track]');
    this.slides = [...this.track.children];
    this.index = 0;
    this.dialog = this.querySelector('dialog');
    this.updateArrows();
    this.updateSceneAccessibility();
    this.setupRatingPreviews();

    // Mouse drag on the gallery and the enlarged view. Loaded apart: if it fails, arrows and swipe still work.
    this.track.addEventListener('pointerdown', () => { this.dragStartIndex = this.index; this.requestedIndex = undefined; });
    this.dialog?.querySelector('[data-lightbox-track]')?.addEventListener('pointerdown', () => {
      this.lightboxDragStart = this.lightboxIndex();
    });
    import('./pdp-drag.js')
      .then(({ enableDrag }) => {
        enableDrag(this.track, {
          onRelease: (direction) => {
            const nearest = Math.round(this.track.scrollLeft / Math.max(1, this.track.clientWidth));
            this.goTo(direction === 0 ? nearest : this.dragStartIndex + direction);
          },
        });
        if (this.lightboxTrack) {
          enableDrag(this.lightboxTrack, {
            onRelease: (direction) => {
              const total = this.lightboxTrack.children.length;
              const target = direction === 0 ? this.lightboxIndex() : this.lightboxDragStart + direction;
              const clamped = Math.max(0, Math.min(total - 1, target));
              this.lightboxTrack.scrollTo({ left: clamped * this.lightboxTrack.clientWidth, behavior: 'smooth' });
            },
          });
        }
      })
      .catch(() => {});

    this.querySelector('[data-prev]')?.addEventListener('click', () => this.goTo(this.index - 1));
    this.querySelector('[data-next]')?.addEventListener('click', () => this.goTo(this.index + 1));
    this.querySelectorAll('[data-thumb]').forEach((thumb) =>
      thumb.addEventListener('click', () => this.goTo(Number(thumb.dataset.thumb)))
    );
    this.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') this.setAttribute('data-preview-dismissed', '');
    });
    this.querySelectorAll('.pdp-story-nav__button').forEach((button) => {
      ['pointerenter', 'focus'].forEach((type) => button.addEventListener(type, () => this.removeAttribute('data-preview-dismissed')));
    });
    this.track.addEventListener('scroll', () => requestAnimationFrame(() => this.syncIndex()), { passive: true });
    this.track.addEventListener('keydown', (event) => {
      if (event.key === 'ArrowRight') { event.preventDefault(); this.goTo(this.index + 1); }
      if (event.key === 'ArrowLeft') { event.preventDefault(); this.goTo(this.index - 1); }
    });

    if (this.dialog) {
      this.lightboxTrack = this.dialog.querySelector('[data-lightbox-track]');
      this.querySelector('[data-zoom]')?.addEventListener('click', () => this.openLightbox());
      this.dialog.querySelector('[data-close]').addEventListener('click', () => this.dialog.close());
      this.dialog.addEventListener('close', () => {
        lockScroll(false);
        this.goTo(this.lightboxIndex(), 'instant');
        this.querySelector('[data-zoom]')?.focus();
      });
      this.lightboxTrack.addEventListener(
        'scroll',
        () => requestAnimationFrame(() => {
          this.dialog.querySelector('[data-lightbox-current]').textContent = this.lightboxIndex() + 1;
          this.updateArrows();
        }),
        { passive: true }
      );
      // Arrows and keyboard in the enlarged view: with a mouse, swiping is not an option.
      const lightboxGo = (step) => {
        const total = this.lightboxTrack.children.length;
        const target = Math.max(0, Math.min(total - 1, this.lightboxIndex() + step));
        this.lightboxTrack.scrollTo({ left: target * this.lightboxTrack.clientWidth, behavior: 'smooth' });
      };
      this.dialog.querySelector('[data-lightbox-prev]')?.addEventListener('click', () => lightboxGo(-1));
      this.dialog.querySelector('[data-lightbox-next]')?.addEventListener('click', () => lightboxGo(1));
      this.dialog.addEventListener('keydown', (event) => {
        if (event.key === 'ArrowLeft') { event.preventDefault(); lightboxGo(-1); }
        if (event.key === 'ArrowRight') { event.preventDefault(); lightboxGo(1); }
      });
    }
  }

  goTo(index, behavior = 'smooth') {
    if (matchMedia('(prefers-reduced-motion: reduce)').matches) behavior = 'instant';
    const target = Math.max(0, Math.min(this.slides.length - 1, index));
    this.requestedIndex = target;
    this.track.scrollTo({ left: this.slides[target].offsetLeft - this.slides[0].offsetLeft, behavior });
  }

  setupRatingPreviews() {
    this.querySelectorAll('.pdp-story__reviews').forEach((link) => {
      const preview = link.querySelector('.pdp-rating-preview');
      if (!preview) return;
      const close = () => { preview.hidden = true; };
      const open = () => {
        const target = link.getAttribute('href');
        if (!target?.startsWith('#')) return;
        const widget = document.getElementById(target.slice(1));
        const rows = [...(widget?.querySelectorAll('.jdgm-histogram__row[data-frequency]') || [])]
          .map((row) => ({ rating: Number(row.dataset.rating), count: Number(row.dataset.frequency) }))
          .filter((row) => Number.isInteger(row.rating) && row.rating >= 1 && row.rating <= 5 && Number.isInteger(row.count) && row.count >= 0)
          .sort((a, b) => b.rating - a.rating);
        const total = rows.reduce((sum, row) => sum + row.count, 0);
        if (rows.length !== 5 || new Set(rows.map((row) => row.rating)).size !== 5 || !total) return;
        const content = preview.querySelector('[data-rating-rows]');
        content.replaceChildren(...rows.map(({ rating, count }) => {
          const percent = Math.round(count / total * 100);
          const row = document.createElement('span');
          row.className = 'pdp-rating-preview__row';
          const label = document.createElement('span');
          label.textContent = `${rating} ★`;
          const bar = document.createElement('span');
          bar.className = 'pdp-rating-preview__bar';
          bar.setAttribute('aria-hidden', 'true');
          const fill = document.createElement('span');
          fill.className = 'pdp-rating-preview__fill';
          fill.style.width = `${percent}%`;
          bar.append(fill);
          const value = document.createElement('span');
          value.textContent = `${percent}% (${count})`;
          row.append(label, bar, value);
          return row;
        }));
        preview.hidden = false;
      };
      link.addEventListener('mouseenter', open);
      link.addEventListener('focus', open);
      link.addEventListener('mouseleave', () => { if (document.activeElement !== link) close(); });
      link.addEventListener('blur', close);
      link.addEventListener('click', close);
      link.addEventListener('keydown', (event) => { if (event.key === 'Escape') close(); });
    });
  }

  syncIndex() {
    const index = Math.round(this.track.scrollLeft / this.track.clientWidth);
    if (index === this.requestedIndex) this.requestedIndex = undefined;
    if (index === this.index) return;
    this.index = index;
    this.updateSceneAccessibility();
    const current = this.querySelector('[data-current]');
    if (current) current.textContent = index + 1;
    this.querySelectorAll('.pdp-gallery__dot').forEach((dot, position) => {
      dot.toggleAttribute('data-active', position === index);
    });
    this.querySelectorAll('[data-thumb]').forEach((thumb) => {
      thumb.setAttribute('aria-current', String(Number(thumb.dataset.thumb) === index));
    });
    if (!this.classList.contains('pdp-gallery--story')) {
      this.querySelector(`[data-thumb="${index}"]`)?.scrollIntoView({ block: 'nearest', inline: 'nearest' });
    }
    this.updateArrows();
  }

  // The first image has no "previous" arrow and the last one no "next" arrow.
  updateSceneAccessibility() {
    this.slides.forEach((slide, position) => {
      const copy = slide.querySelector('.pdp-story__copy');
      if (copy) copy.inert = position !== this.index;
    });
  }

  updateArrows() {
    const last = this.slides.length - 1;
    const prev = this.querySelector('[data-prev]');
    const next = this.querySelector('[data-next]');
    if (prev) prev.hidden = this.index <= 0;
    if (next) next.hidden = this.index >= last;
    if (this.lightboxTrack) {
      const lightboxIndex = this.lightboxIndex();
      const lightboxLast = this.lightboxTrack.children.length - 1;
      const lightboxPrev = this.dialog.querySelector('[data-lightbox-prev]');
      const lightboxNext = this.dialog.querySelector('[data-lightbox-next]');
      if (lightboxPrev) lightboxPrev.hidden = lightboxIndex <= 0;
      if (lightboxNext) lightboxNext.hidden = lightboxIndex >= lightboxLast;
    }
  }

  lightboxIndex() {
    return Math.round(this.lightboxTrack.scrollLeft / Math.max(1, this.lightboxTrack.clientWidth));
  }

  openLightbox() {
    const index = this.requestedIndex ?? this.index;
    this.dialog.showModal();
    lockScroll(true);
    this.lightboxTrack.scrollTo({ left: index * this.lightboxTrack.clientWidth, behavior: 'instant' });
    this.dialog.querySelector('[data-lightbox-current]').textContent = index + 1;
    this.updateArrows();
  }
}
define('pdp-gallery', PdpGallery);
